import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { AgGridReact } from 'ag-grid-react';
import {
  ColDef,
  FirstDataRenderedEvent,
  GetDetailRowDataParams,
  GridOptions,
  ICellRendererParams,
  RowDataUpdatedEvent,
  ValueFormatterParams,
  ValueGetterParams,
} from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-material.css';
import './OOCReportTable.pcss';
import { addWeeks, getWeek, startOfWeek } from 'date-fns';
import { formatDateCustom } from 'modules/I18N';
import { Store } from 'components/common/types/Store.types';
import { HeaderBackgroundColor, TableHeader, TextColor } from 'components/common/TableHeader';
import { OOCFrameOccupancy, OOCReportCampaignDetails } from 'components/common/types/DirectSalesCampaign.types';
import { getLocaleCode } from 'store/publisher/selectors';
import { CampaignCellRenderer } from './CampaignCellRenderer';

const MAX_DETAIL_TABLE_HEIGHT = 320;
const HEADER_HEIGHT = 56;
const ROW_HEIGHT = 47;
const NUMBER_OF_WEEKS_TO_DISPLAY = 4;

const DEFAULT_HEADER_COMPONENT_PARAMS = {
  backgroundColor: HeaderBackgroundColor.WHITE,
  textColor: TextColor.SECONDARY,
  sortable: true,
  justifyContent: 'justify-start gap-1 py-0',
};

const getCampaignColumnDef = ({
  field,
  label,
  subLabel,
  colId,
  width,
  valueFormatter,
  valueGetter,
  cellRenderer,
}: {
  field: string;
  label: string;
  subLabel?: string;
  colId?: string;
  width?: number;
  valueFormatter?: (params: ValueFormatterParams) => string;
  valueGetter?: (params: ValueGetterParams) => unknown;
  cellRenderer?: ColDef['cellRenderer'];
}): ColDef => ({
  field,
  headerName: label,
  sortable: true,
  headerComponent: TableHeader,
  headerComponentParams: {
    ...DEFAULT_HEADER_COMPONENT_PARAMS,
    label,
    subLabel,
  },
  ...(colId && { colId }),
  ...(width && { width }),
  ...(valueFormatter && { valueFormatter }),
  ...(valueGetter && { valueGetter }),
  ...(cellRenderer && { cellRenderer }),
});

const getFrameDetailsColumnDef = ({
  field,
  label,
  minWidth,
  valueGetter,
}: {
  field: string;
  label: string;
  minWidth?: number;
  valueGetter?: (params: ValueGetterParams) => unknown;
}): ColDef => ({
  field,
  headerComponent: TableHeader,
  headerComponentParams: { ...DEFAULT_HEADER_COMPONENT_PARAMS, label },
  minWidth,
  valueGetter,
});

export const OOCReportTable: React.FC = () => {
  const localeCode = useSelector(getLocaleCode);
  const campaignsWithOOCFrames = useSelector((state: Store) => state.directSalesOOC.campaignsWithOOCFrames);

  const today = useMemo(() => new Date(), []);

  const weekColumns = useMemo(() => {
    return Array.from({ length: NUMBER_OF_WEEKS_TO_DISPLAY }, (_, index) => {
      const date = addWeeks(today, index);

      return {
        weekNumber: getWeek(date, { weekStartsOn: 1 }),
        weekStartDate: startOfWeek(date, { weekStartsOn: 1 }),
      };
    });
  }, [today]);

  const rowData = useMemo(() => {
    return campaignsWithOOCFrames.map((campaign) => {
      return {
        ...campaign,
        frameOccupancies: campaign.frameOccupancies.slice(0, NUMBER_OF_WEEKS_TO_DISPLAY),
      };
    });
  }, [campaignsWithOOCFrames]);

  const columnDefs: ColDef[] = [
    {
      field: 'campaignId',
      headerName: 'Campaigns',
      pinned: 'left',
      minWidth: 300,
      cellRenderer: CampaignCellRenderer,
      headerComponent: TableHeader,
      headerComponentParams: {
        ...DEFAULT_HEADER_COMPONENT_PARAMS,
        label: 'Campaigns',
        sortable: false,
      },
    },
    getCampaignColumnDef({ field: 'salesTeam', label: 'Sales team' }),
    getCampaignColumnDef({ field: 'salesPerson', label: 'Sales person' }),
    getCampaignColumnDef({ field: 'adminPerson', label: 'Admin person' }),
    getCampaignColumnDef({ field: 'brand', label: 'Brand' }),
    getCampaignColumnDef({ field: 'status', label: 'Status' }),
    getCampaignColumnDef({
      field: 'campaignStartDate',
      label: 'Start date',
      valueFormatter: (params: ValueFormatterParams) =>
        formatDateCustom(params.value, { day: '2-digit', month: '2-digit', year: 'numeric' }, localeCode),
    }),
    getCampaignColumnDef({
      field: 'campaignEndDate',
      label: 'End date',
      valueFormatter: (params: ValueFormatterParams) =>
        formatDateCustom(params.value, { day: '2-digit', month: '2-digit', year: 'numeric' }, localeCode),
    }),
    ...weekColumns.map((weekColumn) =>
      getCampaignColumnDef({
        colId: `week-${weekColumn.weekNumber}`,
        field: '',
        label: `Week ${weekColumn.weekNumber}`,
        subLabel: formatDateCustom(
          weekColumn.weekStartDate,
          { day: '2-digit', month: '2-digit', year: 'numeric' },
          localeCode,
        ),
        valueGetter: (params: ValueGetterParams) =>
          params.data?.frameOccupancies.find(
            (occupancy: OOCFrameOccupancy) => occupancy.occupancyWeek.week === weekColumn.weekNumber,
          )?.frameCount,
        cellRenderer: (params: ICellRendererParams) => {
          if (params.value != null) return params.value;

          const lastCampaignId = rowData[rowData.length - 1]?.campaignId;
          if (!lastCampaignId || params.data?.campaignId !== lastCampaignId) return null;

          const lastEmptyWeekNumber = [...weekColumns]
            .reverse()
            .find(
              (column) =>
                !params.data?.frameOccupancies?.some(
                  (occupancy: OOCFrameOccupancy) => occupancy.occupancyWeek.week === column.weekNumber,
                ),
            )?.weekNumber;

          if (weekColumn.weekNumber !== lastEmptyWeekNumber) return null;

          return <span className="text-lg leading-none">🎃</span>;
        },
        width: 144,
      }),
    ),
  ];

  const onRowDataUpdated = (params: RowDataUpdatedEvent): void => {
    requestAnimationFrame(() => {
      params.columnApi.autoSizeAllColumns(false);
    });
  };

  const gridOptions: GridOptions = {
    suppressMovableColumns: true,
    suppressBrowserResizeObserver: true,
    defaultColDef: {
      resizable: true,
      suppressMenu: true,
    },
    onFirstDataRendered: (params: FirstDataRenderedEvent): void => {
      const columnIds =
        params.columnApi.getColumns()?.reduce<string[]>((acc, column) => {
          if (!column.isPinned()) acc.push(column.getColId());
          return acc;
        }, []) || [];

      params.columnApi.autoSizeColumns(columnIds, false);
    },
    onRowDataUpdated,
    getRowHeight: (params) => {
      if (params.node.detail) {
        const rows = params.data?.frameDetails?.length ?? 0;
        return Math.min(HEADER_HEIGHT + ROW_HEIGHT + rows * ROW_HEIGHT, MAX_DETAIL_TABLE_HEIGHT);
      }
      return ROW_HEIGHT;
    },
  };
  const detailCellRendererParams = useMemo(() => {
    return {
      detailGridOptions: {
        suppressMovableColumns: true,
        suppressBrowserResizeObserver: true,
        defaultColDef: {
          resizable: true,
          suppressMenu: true,
        },
        columnDefs: [
          getFrameDetailsColumnDef({ field: 'routeFrameCode', label: 'Route frame code' }),
          getFrameDetailsColumnDef({ field: 'furnitureId', label: 'Furniture ID' }),
          getFrameDetailsColumnDef({ field: 'productFormat', label: 'Product format' }),
          getFrameDetailsColumnDef({ field: 'businessArea', label: 'Business area' }),
          getFrameDetailsColumnDef({ field: 'sizeCode', label: 'Size code' }),
          getFrameDetailsColumnDef({ field: 'mediaType', label: 'Media type' }),
          getFrameDetailsColumnDef({ field: 'routeTown', label: 'Route town' }),
          getFrameDetailsColumnDef({ field: 'address', label: 'Address' }),
        ],
        onFirstDataRendered: (params: FirstDataRenderedEvent) => {
          requestAnimationFrame(() => {
            const columnIds =
              params.columnApi.getColumns()?.reduce<string[]>((acc, column) => {
                const id = column.getColId();

                if (id !== 'routeFrameCode') acc.push(id);
                return acc;
              }, []) || [];

            params.columnApi.autoSizeColumns(columnIds, false);
          });
        },
        onRowDataUpdated,
      },
      getDetailRowData: (params: GetDetailRowDataParams<OOCReportCampaignDetails>) => {
        params.successCallback(params.data.frameDetails);
      },
    };
  }, []);

  return (
    <div className="ag-theme-material ooc-report-table h-full" data-test-id="ooc-report-table">
      <AgGridReact
        rowData={rowData}
        columnDefs={columnDefs}
        gridOptions={gridOptions}
        masterDetail
        detailCellRendererParams={detailCellRendererParams}
      />
    </div>
  );
};
